<template>
    <div class="relative">
        <label class="relative block" ref="reference">
            <input
                class="h-10 w-full flex items-center gap-2 text-left pl-3 text-slate-500 relative bg-white rounded border border-slate-200 py-1.5 placeholder:text-slate-500 placeholder:text-sm transition-colors text-sm focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                v-model="programQuery"
                @focus="openDropdown"
                :class="
                    tripFormErrors?.payment_program_id ? '!border-red-500' : ''
                "
            />
            <div
                class="w-full flex items-center gap-2 absolute pr-14 left-2 top-[50%] -translate-y-[calc(50%-0.125rem)] whitespace-nowrap truncate min-w-0"
            >
                <ImageLoader v-if="program">
                    <template #image>
                        <img
                            :src="`${cdnUrl}/${program?.program_image?.path}/_small/${program?.program_image?.file_name}`"
                            :alt="program?.name"
                            class="max-w-10 max-h-5 rounded-sm"
                        />
                    </template>
                    <template #error>
                        <Icon icon="points" class="text-slate-600 w-8 h-5" />
                    </template>
                </ImageLoader>
                <span class="truncate w-full overflow-hidden">
                    {{ program?.id ? program?.name : '' }}
                </span>
            </div>
            <button
                class="block text-red-600 w-5 h-5 absolute right-2 top-1/2 -translate-y-1/2 z-10"
                v-if="program"
                @click="clearProgram()"
            >
                <Icon icon="close" />
            </button>
        </label>
        <div
            class="text-red-500 text-xs flex items-center gap-2 py-1.5"
            role="alert"
            v-if="tripFormErrors?.payment_program_id"
        >
            <Icon icon="caution" />
            Please choose a points currency
        </div>

        <Teleport to="body">
            <transition name="fade">
                <div
                    class="max-h-48 p-2 overflow-y-auto bg-white rounded-lg shadow-sm border border-slate-200 absolute z-[99999]"
                    v-if="showList"
                    ref="floating"
                >
                    <label
                        class="w-full flex items-center gap-2 px-3 py-2 text-slate-800 text-sm bg-white rounded hover:bg-slate-100 transition-colors mb-1 cursor-pointer"
                        v-for="program in programsFiltered"
                        :key="program.id"
                        @click="
                            showList = false;
                            programQuery = null;
                        "
                    >
                        <input
                            class="sr-only"
                            type="radio"
                            name="connected_account_id"
                            :value="program.id"
                            v-model="activeRedemption.payment_program_id"
                        />
                        <div class="w-12 flex justify-center">
                            <ImageLoader>
                                <template #image>
                                    <img
                                        :src="`${cdnUrl}/${program.image?.path}/_small/${program.image?.file_name}`"
                                        :alt="program?.label"
                                        class="max-w-10 max-h-5 rounded-sm"
                                    />
                                </template>
                                <template #error>
                                    <Icon
                                        icon="points"
                                        class="text-slate-600 w-8 h-5"
                                    />
                                </template>
                            </ImageLoader>
                        </div>
                        {{ program?.label }}
                    </label>
                    <div
                        v-if="programsFiltered.length === 0"
                        class="px-3 py-2 text-sm text-slate-800"
                    >
                        No programs found
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>
<script setup>
import { storeToRefs } from 'pinia';
import { onMounted, onUnmounted, ref, watch, computed, nextTick } from 'vue';
import { useTripsStore } from '@/store/trips';
import { computePosition, flip, shift, offset } from '@floating-ui/vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';

const tripsStore = useTripsStore();
const { activeRedemption, redemptionTypes, programs, program, tripFormErrors } =
    storeToRefs(tripsStore);

const programQuery = ref(null);
const showList = ref(false);
const reference = ref(null);
const floating = ref(null);

const handleGlobalClick = (event) => {
    if (
        (floating.value && floating.value.contains(event.target)) || 
        (reference.value && reference.value.contains(event.target))
    ) {
        return;
    }
    
    showList.value = false;
};

const openDropdown = () => {
    showList.value = true;
};

const updatePosition = () => {
    if (reference.value && floating.value) {
        computePosition(reference.value, floating.value, {
            placement: 'bottom-start',
            middleware: [offset(4), flip(), shift()],
        }).then(({ x, y }) => {
            Object.assign(floating.value.style, {
                left: `${x}px`,
                top: `${y}px`,
                minWidth: `${reference.value.offsetWidth}px`,
            });
        });
    }
};

onMounted(() => {
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    
    document.addEventListener('click', handleGlobalClick);
    document.addEventListener('touchend', handleGlobalClick);
});

onUnmounted(() => {
    window.removeEventListener('scroll', updatePosition, true);
    window.removeEventListener('resize', updatePosition);
    document.removeEventListener('click', handleGlobalClick);
    document.removeEventListener('touchend', handleGlobalClick);
});

watch(
    () => showList.value,
    (newValue) => {
        if (newValue) {
            nextTick(() => {
                updatePosition();
            });
        }
    }
);

const redemptionType = computed(() => {
    return redemptionTypes.value.find(
        (redemptionType) =>
            redemptionType.id === activeRedemption.value.redemption_type_id
    );
});

const programsFiltered = computed(() => {
    let programType = redemptionType.value?.slug;

    if (programType === 'flight') {
        programType = 'airline';
    }
    const prefilteredPrograms = programs.value
        .map((program) => {
            return {
                label: program.name,
                value: program.type,
                id: program.id,
                image: program.program_image,
            };
        })
        .filter(
            (program) =>
                program.value === programType ||
                program.value === "flexible_currency" ||
                redemptionType.value?.slug === 'other' ||
                redemptionType.value?.slug === 'rental-car' ||
                redemptionType.value?.slug === 'train'
        );

    return prefilteredPrograms.filter((program) => {
        return programQuery.value && programQuery.value.length > 0
            ? program.label
                  .toLowerCase()
                  .replace('.', '')
                  .includes(programQuery.value.toLowerCase())
            : true;
    });
});

const clearProgram = () => {
    activeRedemption.value.payment_program_id = null;
};

const cdnUrl = computed(() => {
    return window.cdnUrl;
});
</script>
